// /settings/mfa — MFA Enforcement (Sprint G3.13)
//
// Admin console for two-factor enrollment posture across the user base.
// Per-user actions: toggle mfa_required, enable a TOTP setup slot, reset enrollment.
// Bulk action: require MFA for every eligible user in a chosen role.
// SHARED vertical. OWNER (50) tenant; MODERATOR/ADMIN/SUPER_ADMIN cross-tenant via picker.

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  PageContainer,
  Badge,
  Button,
  Select,
  Input,
  Toggle,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Pagination,
  Modal,
  ConfirmDialog,
  Tooltip,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';
import { userService } from '@/services/user.service';
import { tenantService } from '@/services/tenant.service';
import { mfaService } from '@/services/mfa.service';
import { ROLE_LEVELS, ROLE_BADGE_VARIANT } from '@/types/user';
import type { User, UserRole } from '@/types/user';
import { ApiError } from '@/lib/api-client';
import { GLASS, GLASS_FILTER, GLASS_FORM, cn, toneClasses } from '@/constants/design-tokens';

// ─── Constants ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;
const PLATFORM_ROLES: UserRole[] = ['SUPER_ADMIN', 'ADMIN', 'MODERATOR'];

type MfaStatusFilter = '' | 'enrolled' | 'not_enrolled' | 'required' | 'optional' | 'non_compliant';

const MFA_STATUS_OPTIONS: { value: MfaStatusFilter; label: string }[] = [
  { value: '', label: 'All MFA states' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'not_enrolled', label: 'Not enrolled' },
  { value: 'required', label: 'Required' },
  { value: 'optional', label: 'Optional' },
  { value: 'non_compliant', label: 'Non-compliant' },
];

const ALL_ROLES: { value: string; label: string }[] = [
  { value: '', label: 'All roles' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MODERATOR', label: 'Moderator' },
  { value: 'OWNER', label: 'Owner' },
  { value: 'MANAGER', label: 'Manager' },
  { value: 'CAPTAIN', label: 'Captain' },
  { value: 'CASHIER', label: 'Cashier' },
];

// ─── Icons ─────────────────────────────────────────────────────────────────

function ShieldIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function ShieldCheckIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 12.75L11.25 15 15 9.75M21 12c0 5.523-4.477 10-10 10S1 17.523 1 12 5.477 2 11 2c2.027 0 3.92.604 5.5 1.643" />
    </svg>
  );
}

function LockIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 11v4m6-7V7a6 6 0 10-12 0v1m12 0H6a2 2 0 00-2 2v9a2 2 0 002 2h12a2 2 0 002-2v-9a2 2 0 00-2-2z" />
    </svg>
  );
}

function AlertIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    </svg>
  );
}

function UsersIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  );
}

function KeyIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 7a4 4 0 11-4 4m0 0L4 18m4-4l3 3m4-7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function ResetIcon({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 4v5h5M20 20v-5h-5M5 9a7 7 0 0112-3.5L20 8M19 15a7 7 0 01-12 3.5L4 16" />
    </svg>
  );
}

function ShieldEmptyIcon() {
  return (
    <svg className="h-14 w-14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function initials(name: string): string {
  return name.split(' ').slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('');
}

function formatRelative(iso?: string | null): string {
  if (!iso) return 'Never';
  try {
    const then = new Date(iso).getTime();
    const diff = Date.now() - then;
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function formatAbsolute(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

interface AuthDecision {
  allowed: boolean;
  reason?: string;
}

/**
 * Mirrors the server-side rules in users.service.ts:135-180.
 * Returning the reason lets the UI explain *why* an action is disabled instead
 * of leaving the user staring at a gray button.
 */
function canMutate(
  callerRole: UserRole | undefined,
  callerId: string | undefined,
  target: User,
): AuthDecision {
  if (!callerRole || !callerId) return { allowed: false, reason: 'No active session.' };
  if (callerId === target.id) {
    return { allowed: false, reason: 'You cannot modify your own MFA from this console — use Settings → Security.' };
  }
  const callerLevel = ROLE_LEVELS[callerRole] ?? 0;
  if (callerLevel < ROLE_LEVELS.OWNER) {
    return { allowed: false, reason: 'OWNER role or higher required.' };
  }

  const targetIsPlatform = PLATFORM_ROLES.includes(target.role);

  if (callerRole === 'OWNER') {
    if (target.role === 'OWNER') {
      return { allowed: false, reason: 'OWNERs cannot modify other OWNERs.' };
    }
    if (targetIsPlatform) {
      return { allowed: false, reason: 'Platform users are managed by the platform team.' };
    }
    return { allowed: true };
  }

  if (callerRole === 'MODERATOR' || callerRole === 'ADMIN') {
    if (targetIsPlatform) {
      return { allowed: false, reason: 'Only SUPER_ADMIN can flag platform users.' };
    }
    return { allowed: true };
  }

  // SUPER_ADMIN — anyone except self (handled above)
  return { allowed: true };
}

// ─── Bulk Modal ─────────────────────────────────────────────────────────────

interface BulkResult {
  userId: string;
  name: string;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

function BulkRequireModal({
  open,
  onClose,
  onCompleted,
  callerRole,
  callerId,
  tenantScope,
}: {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
  callerRole: UserRole;
  callerId: string;
  tenantScope?: string;
}) {
  const { toast } = useToast();

  const [role, setRole] = useState<string>('CASHIER');
  const [scopeOnlyMissing, setScopeOnlyMissing] = useState(true);
  const [preview, setPreview] = useState<User[] | null>(null);
  const [isFetchingPreview, setIsFetchingPreview] = useState(false);
  const [results, setResults] = useState<BulkResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isDone, setIsDone] = useState(false);

  // Roles a caller can target in bulk
  const targetRoleOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    if (callerRole === 'OWNER') {
      opts.push({ value: 'MANAGER', label: 'Manager' });
      opts.push({ value: 'CAPTAIN', label: 'Captain' });
      opts.push({ value: 'CASHIER', label: 'Cashier' });
    } else if (callerRole === 'MODERATOR' || callerRole === 'ADMIN') {
      opts.push({ value: 'OWNER', label: 'Owner' });
      opts.push({ value: 'MANAGER', label: 'Manager' });
      opts.push({ value: 'CAPTAIN', label: 'Captain' });
      opts.push({ value: 'CASHIER', label: 'Cashier' });
    } else if (callerRole === 'SUPER_ADMIN') {
      opts.push({ value: 'ADMIN', label: 'Admin' });
      opts.push({ value: 'MODERATOR', label: 'Moderator' });
      opts.push({ value: 'OWNER', label: 'Owner' });
      opts.push({ value: 'MANAGER', label: 'Manager' });
      opts.push({ value: 'CAPTAIN', label: 'Captain' });
      opts.push({ value: 'CASHIER', label: 'Cashier' });
    }
    return opts;
  }, [callerRole]);

  // Default to first allowed role whenever the modal opens
  useEffect(() => {
    if (open && targetRoleOptions.length > 0) {
      setRole(targetRoleOptions[0].value);
      setPreview(null);
      setResults([]);
      setIsDone(false);
    }
  }, [open, targetRoleOptions]);

  const fetchPreview = useCallback(async () => {
    setIsFetchingPreview(true);
    try {
      const res = await userService.list({
        role,
        is_active: 'true',
        limit: 200,
        ...(tenantScope ? { tenant_id: tenantScope } : {}),
        ...(scopeOnlyMissing ? { mfa_required: 'false' as const } : {}),
      });
      // Mirror the server-side authorization filter so the count we show
      // matches what the bulk endpoint will actually flip.
      const eligible = res.data
        .filter((u) => u.id !== callerId)
        .filter((u) => canMutate(callerRole, callerId, u).allowed);
      setPreview(eligible);
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to preview users.',
        variant: 'error',
      });
      setPreview([]);
    } finally {
      setIsFetchingPreview(false);
    }
  }, [role, scopeOnlyMissing, tenantScope, callerRole, callerId, toast]);

  useEffect(() => {
    if (open && role) {
      void fetchPreview();
    }
  }, [open, role, scopeOnlyMissing, fetchPreview]);

  const runBulk = useCallback(async () => {
    if (!preview || preview.length === 0) return;
    setIsRunning(true);
    const initial: BulkResult[] = preview.map((u) => ({
      userId: u.id,
      name: u.name,
      status: 'pending',
    }));
    setResults(initial);

    try {
      const result = await mfaService.bulkRequire({
        role,
        skip_already_required: scopeOnlyMissing,
        ...(tenantScope ? { tenant_id: tenantScope } : {}),
      });

      const succeeded = new Set(result.succeeded);
      const skipped = new Map(result.skipped.map((s) => [s.id, s.reason]));
      const failed = new Map(result.failed.map((f) => [f.id, f.error]));

      setResults((prev) => prev.map((r) => {
        if (succeeded.has(r.userId)) return { ...r, status: 'success' };
        if (failed.has(r.userId)) return { ...r, status: 'error', error: failed.get(r.userId) };
        if (skipped.has(r.userId)) return { ...r, status: 'success', error: undefined };
        // Server may have surfaced users we did not preview (mid-flight signups).
        // We did not show them to the operator, so don't mark them either.
        return r;
      }));
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Bulk request failed';
      setResults((prev) => prev.map((r) => ({ ...r, status: 'error', error: message })));
      toast({ title: message, variant: 'error' });
    } finally {
      setIsRunning(false);
      setIsDone(true);
      onCompleted();
    }
  }, [preview, role, scopeOnlyMissing, tenantScope, onCompleted, toast]);

  const successCount = results.filter((r) => r.status === 'success').length;
  const errorCount = results.filter((r) => r.status === 'error').length;
  const pendingCount = results.filter((r) => r.status === 'pending').length;
  const progressPct = results.length > 0 ? Math.floor(((successCount + errorCount) / results.length) * 100) : 0;

  const violetTone = toneClasses('violet');

  return (
    <Modal
      open={open}
      onClose={() => { if (!isRunning) onClose(); }}
      title="Require MFA by Role"
      variant="default"
      closeOnBackdrop={!isRunning}
      closeOnEscape={!isRunning}
    >
      <div className="space-y-4">
        <p className="text-sm text-neutral-600 dark:text-neutral-400">
          Mark every eligible user in the selected role as MFA-required. Each user
          will be forced to enroll on next login and existing sessions will be
          invalidated. Self and protected peers are skipped automatically.
        </p>
        <p className="text-sm text-amber-700 dark:text-amber-300">
          This is a one-time run, not a tenant-wide setting: only users who exist now are
          covered. Users created later are not required automatically — mark them on
          their row or run this again.
        </p>

        <div className={cn(GLASS_FORM, 'p-4 space-y-3')}>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1.5">
              Role to enforce
            </label>
            <Select
              options={targetRoleOptions}
              value={role}
              onChange={(val) => setRole(typeof val === 'string' ? val : val[0] ?? 'CASHIER')}
              disabled={isRunning || isDone}
            />
          </div>
          <Toggle
            checked={scopeOnlyMissing}
            onChange={setScopeOnlyMissing}
            disabled={isRunning || isDone}
            label="Skip users that are already marked as required"
            description="Recommended — avoids re-issuing the requirement and re-invalidating sessions."
          />
        </div>

        {/* Preview / progress */}
        {isFetchingPreview ? (
          <div className={cn(GLASS, 'p-4')}>
            <Skeleton variant="text" width="60%" height={14} />
            <div className="mt-2 space-y-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} variant="text" width="100%" height={12} />
              ))}
            </div>
          </div>
        ) : preview && results.length === 0 ? (
          <div className={cn(GLASS, 'p-4')}>
            <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
              {preview.length === 0
                ? scopeOnlyMissing
                  ? `No ${role.toLowerCase()}s left to enforce — every eligible user in scope is already required.`
                  : `No eligible ${role.toLowerCase()}s found in this scope.`
                : `${preview.length.toLocaleString('en-IN')} user${preview.length === 1 ? '' : 's'} will be required to enroll`}
            </p>
            {preview.length > 0 && (
              <ul className="mt-2 max-h-40 overflow-y-auto space-y-1 text-xs">
                {preview.slice(0, 50).map((u) => (
                  <li key={u.id} className="flex items-center gap-2 py-1 px-2 rounded even:bg-neutral-50/60 dark:even:bg-neutral-800/40">
                    <span className="font-medium text-neutral-700 dark:text-neutral-200 truncate">{u.name}</span>
                    <span className="font-mono text-neutral-400 dark:text-neutral-500 truncate">{u.email}</span>
                  </li>
                ))}
                {preview.length > 50 && (
                  <li className="text-center text-neutral-400 dark:text-neutral-500 italic py-1">
                    + {(preview.length - 50).toLocaleString('en-IN')} more
                  </li>
                )}
              </ul>
            )}
          </div>
        ) : null}

        {results.length > 0 && (
          <div className={cn(GLASS, 'p-4')}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                {isDone ? 'Completed' : 'Applying…'}
              </span>
              <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                {(successCount + errorCount).toLocaleString('en-IN')} / {results.length.toLocaleString('en-IN')}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
              <div
                className={cn(
                  'h-full transition-all duration-300',
                  errorCount > 0 ? 'bg-gradient-to-r from-violet-500 to-rose-500' : 'bg-gradient-to-r from-violet-500 to-emerald-500',
                )}
                style={{ width: `${progressPct}%` }}
                role="progressbar"
                aria-valuenow={progressPct}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs">
              <span className="text-emerald-700 dark:text-emerald-400 font-mono tabular-nums">{successCount} ok</span>
              {errorCount > 0 && (
                <span className="text-rose-700 dark:text-rose-400 font-mono tabular-nums">{errorCount} failed</span>
              )}
              {pendingCount > 0 && (
                <span className="text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">{pendingCount} queued</span>
              )}
            </div>

            {isDone && errorCount > 0 && (
              <details className="mt-3">
                <summary className="text-xs font-medium text-rose-700 dark:text-rose-400 cursor-pointer">
                  View {errorCount} failure{errorCount === 1 ? '' : 's'}
                </summary>
                <ul className="mt-2 max-h-32 overflow-y-auto space-y-1 text-xs">
                  {results.filter((r) => r.status === 'error').map((r) => (
                    <li key={r.userId} className="flex items-start gap-2 py-1 px-2 rounded bg-rose-50/60 dark:bg-rose-900/20">
                      <span className="font-medium text-neutral-700 dark:text-neutral-200">{r.name}</span>
                      <span className="font-mono text-rose-700 dark:text-rose-400 ml-auto">{r.error}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={onClose}
            disabled={isRunning}
          >
            {isDone ? 'Close' : 'Cancel'}
          </Button>
          {!isDone && (
            <Button
              type="button"
              variant="primary"
              size="md"
              onClick={runBulk}
              loading={isRunning}
              disabled={isRunning || isFetchingPreview || !preview || preview.length === 0}
              icon={<LockIcon className="h-4 w-4" />}
              className={cn('!bg-gradient-to-r !from-brand-600 !to-violet-600', violetTone.icon && '')}
            >
              Require MFA for {preview?.length ?? 0} user{preview?.length === 1 ? '' : 's'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

interface Row extends User {
  id: string;
}

interface ConfirmState {
  type: 'require_on' | 'require_off' | 'enable_totp' | 'disable_totp';
  user: User;
}

function MfaEnforcementContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const profile = useAuthStore((s) => s.profile);

  const callerRole = (profile?.role ?? '') as UserRole;
  const callerId = profile?.id ?? '';
  const callerLevel = ROLE_LEVELS[callerRole] ?? 0;
  const isPlatformRole = callerLevel >= ROLE_LEVELS.MODERATOR;

  // Filters
  const [page, setPage] = useState(Number(searchParams.get('page') ?? '1'));
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [searchInput, setSearchInput] = useState(search);
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') ?? '');
  const [mfaFilter, setMfaFilter] = useState<MfaStatusFilter>((searchParams.get('mfa') ?? '') as MfaStatusFilter);
  const [tenantFilter, setTenantFilter] = useState(searchParams.get('tenant_id') ?? '');

  // Data
  const [users, setUsers] = useState<User[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState<{ total: number; enrolled: number; required: number; nonCompliant: number }>({
    total: 0, enrolled: 0, required: 0, nonCompliant: 0,
  });

  // Tenant picker (platform only)
  const [tenants, setTenants] = useState<{ id: string; business_name: string }[]>([]);

  // Modal state
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => { document.title = 'MFA Enforcement — Orio360 Admin'; }, []);

  // Sync URL
  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.q = search;
    if (roleFilter) params.role = roleFilter;
    if (mfaFilter) params.mfa = mfaFilter;
    if (tenantFilter) params.tenant_id = tenantFilter;
    if (page > 1) params.page = String(page);
    setSearchParams(params, { replace: true });
  }, [search, roleFilter, mfaFilter, tenantFilter, page, setSearchParams]);

  // Load tenant list (platform roles only — for cross-tenant scoping)
  useEffect(() => {
    if (!isPlatformRole) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await tenantService.list({ limit: 200 });
        if (!cancelled) {
          setTenants(res.data.map((t) => ({ id: t.id, business_name: t.business_name })));
        }
      } catch {
        // Filter is optional — silent fail.
      }
    })();
    return () => { cancelled = true; };
  }, [isPlatformRole]);

  // Translate the page's compound MFA filter into the two server query params.
  const mfaQueryParams = useMemo(() => {
    switch (mfaFilter) {
      case 'enrolled': return { mfa_enabled: 'true' as const };
      case 'not_enrolled': return { mfa_enabled: 'false' as const };
      case 'required': return { mfa_required: 'true' as const };
      case 'optional': return { mfa_required: 'false' as const };
      case 'non_compliant': return { mfa_required: 'true' as const, mfa_enabled: 'false' as const };
      default: return {};
    }
  }, [mfaFilter]);

  // Bumped after every mutation/bulk completion so the stats effect re-fires
  // without depending on the heavier `users` array (which would refetch
  // stats on every pagination click).
  const [statsVersion, setStatsVersion] = useState(0);
  const refreshStats = useCallback(() => setStatsVersion((v) => v + 1), []);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await userService.list({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        role: roleFilter || undefined,
        ...(tenantFilter ? { tenant_id: tenantFilter } : {}),
        ...mfaQueryParams,
      });
      setUsers(res.data);
      setTotalItems(res.meta.total);
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Failed to load users.',
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [page, search, roleFilter, tenantFilter, mfaQueryParams, toast]);

  useEffect(() => { void fetchUsers(); }, [fetchUsers]);

  // Aggregate stats — single SQL round-trip on the backend, scoped by RLS
  // (tenant callers) or by the tenant picker (platform callers).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await mfaService.getStats(tenantFilter || undefined);
        if (cancelled) return;
        setStats({
          total: res.total,
          enrolled: res.enrolled,
          required: res.required,
          nonCompliant: res.non_compliant,
        });
      } catch {
        // Stats are best-effort.
      }
    })();
    return () => { cancelled = true; };
  }, [tenantFilter, statsVersion]);

  const runMutation = useCallback(async (state: ConfirmState) => {
    setConfirmLoading(true);
    try {
      switch (state.type) {
        case 'require_on':
          await mfaService.setMfaRequired(state.user.id, true);
          toast({ title: `MFA required for ${state.user.name}. Their sessions were signed out.`, variant: 'success' });
          break;
        case 'require_off':
          await mfaService.setMfaRequired(state.user.id, false);
          toast({ title: `MFA no longer required for ${state.user.name}.`, variant: 'success' });
          break;
        case 'enable_totp':
          await mfaService.enableTotpForUser(state.user.id);
          toast({ title: `TOTP setup unlocked for ${state.user.name}. They will enroll on next login.`, variant: 'success' });
          break;
        case 'disable_totp':
          await mfaService.disableTotpForUser(state.user.id);
          toast({ title: `MFA reset for ${state.user.name}. They can re-enroll any time.`, variant: 'success' });
          break;
      }
      setConfirmState(null);
      await fetchUsers();
      refreshStats();
    } catch (err) {
      toast({
        title: err instanceof ApiError ? err.message : 'Action failed.',
        variant: 'error',
      });
    } finally {
      setConfirmLoading(false);
    }
  }, [toast, fetchUsers, refreshStats]);

  // ─── Table columns ─────────────────────────────────────────────────────
  const columns: ColumnDef<Row>[] = useMemo(() => [
    {
      key: 'name',
      header: 'User',
      render: (_v, row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 via-indigo-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
            {initials(row.name)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{row.name}</p>
            <p className="text-xs font-mono text-neutral-500 dark:text-neutral-400 truncate">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (_v, row) => (
        <Badge variant={ROLE_BADGE_VARIANT[row.role] ?? 'draft'} size="sm">{row.role}</Badge>
      ),
    },
    {
      key: 'mfa_enabled',
      header: 'Enrolled',
      render: (_v, row) => row.mfa_enabled ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          Enrolled
        </span>
      ) : (
        <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500 dark:text-neutral-400">
          <span className="h-1.5 w-1.5 rounded-full bg-neutral-300 dark:bg-neutral-600" />
          Not set up
        </span>
      ),
    },
    {
      key: 'mfa_required',
      header: 'Required',
      render: (_v, row) => {
        if (!row.mfa_required) {
          return (
            <span className="text-xs text-neutral-500 dark:text-neutral-400">Optional</span>
          );
        }
        return (
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 dark:text-violet-400">
              <LockIcon className="h-3 w-3" />
              Required
            </span>
            {row.mfa_required_at && (
              <p className="text-[10px] font-mono text-neutral-400 dark:text-neutral-500 mt-0.5" title={formatAbsolute(row.mfa_required_at)}>
                {formatRelative(row.mfa_required_at)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      key: 'last_login',
      header: 'Last login',
      render: (_v, row) => (
        <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400" title={formatAbsolute(row.last_login_at)}>
          {formatRelative(row.last_login_at)}
        </span>
      ),
    },
    {
      key: 'compliance',
      header: 'State',
      render: (_v, row) => {
        if (row.mfa_required && !row.mfa_enabled) {
          return <Badge variant="danger" size="sm">Non-compliant</Badge>;
        }
        if (row.mfa_required && row.mfa_enabled) {
          return <Badge variant="success" size="sm">Hardened</Badge>;
        }
        if (row.mfa_enabled) {
          return <Badge variant="info" size="sm">Voluntary</Badge>;
        }
        return <Badge variant="neutral" size="sm">Open</Badge>;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_v, row) => {
        const decision = canMutate(callerRole, callerId, row);
        const requireToggle = (
          <Toggle
            size="sm"
            checked={!!row.mfa_required}
            disabled={!decision.allowed}
            onChange={(next) => setConfirmState({
              type: next ? 'require_on' : 'require_off',
              user: row,
            })}
            label={row.mfa_required ? 'On' : 'Off'}
          />
        );
        return (
          <div className="flex items-center gap-2">
            {decision.allowed ? requireToggle : (
              <Tooltip content={decision.reason ?? 'Disabled'}>
                <span className="inline-block">{requireToggle}</span>
              </Tooltip>
            )}
            {row.mfa_enabled ? (
              <Tooltip content={decision.allowed ? 'Reset enrollment — clears the existing TOTP secret.' : decision.reason ?? 'Disabled'}>
                <span className="inline-block">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!decision.allowed}
                    onClick={() => setConfirmState({ type: 'disable_totp', user: row })}
                    icon={<ResetIcon className="h-3.5 w-3.5" />}
                    aria-label={`Reset MFA for ${row.name}`}
                  >
                    Reset
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Tooltip content={decision.allowed ? 'Pre-enable a TOTP slot — the user finishes enrollment on next login.' : decision.reason ?? 'Disabled'}>
                <span className="inline-block">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!decision.allowed}
                    onClick={() => setConfirmState({ type: 'enable_totp', user: row })}
                    icon={<KeyIcon className="h-3.5 w-3.5" />}
                    aria-label={`Enable TOTP slot for ${row.name}`}
                  >
                    Enable
                  </Button>
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ], [callerRole, callerId]);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const brandTone = toneClasses('brand');
  const successTone = toneClasses('success');
  const violetTone = toneClasses('violet');
  const dangerTone = toneClasses('danger');

  const tenantOptions = useMemo(() => [
    { value: '', label: 'All tenants' },
    ...tenants.map((t) => ({ value: t.id, label: t.business_name })),
  ], [tenants]);

  return (
    <PageContainer
      title="MFA Enforcement"
      subtitle="Two-factor authentication posture across the workspace"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Settings', href: '/settings' },
        { label: 'MFA Enforcement' },
      ]}
    >
      <div className="animate-fade-in">
        {/* Hero header */}
        <div className="bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 rounded-2xl p-6 text-white shadow-[0_24px_60px_-22px_rgba(99,102,241,0.55)] mb-6 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-12 h-52 w-52 rounded-full bg-black/15 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
                <ShieldIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold leading-tight text-white">MFA Enforcement</h1>
                <p className="text-indigo-100 mt-1 text-sm max-w-xl">
                  Lock down every login with time-based one-time passwords. Mark
                  users as required, pre-provision TOTP slots, or reset enrollment
                  on a lost device.
                </p>
              </div>
            </div>
            {callerLevel >= ROLE_LEVELS.OWNER && (
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={() => setBulkOpen(true)}
                icon={<LockIcon className="h-4 w-4" />}
                className="!bg-white/15 !text-white hover:!bg-white/25 !backdrop-blur-sm border-none shrink-0"
              >
                Require MFA by Role…
              </Button>
            )}
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <button
            type="button"
            onClick={() => { setMfaFilter(''); setPage(1); }}
            aria-label={`Show all ${stats.total.toLocaleString('en-IN')} users`}
            className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden text-left transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400')}
          >
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', brandTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', brandTone.icon)}>
                  <UsersIcon />
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">Total Users</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {stats.total.toLocaleString('en-IN')}
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => { setMfaFilter('enrolled'); setPage(1); }}
            aria-label={`Filter to ${stats.enrolled.toLocaleString('en-IN')} users with MFA enrolled`}
            className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden text-left transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400')}
            style={{ animationDelay: '50ms' }}
          >
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', successTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', successTone.icon)}>
                  <ShieldCheckIcon />
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">MFA Enrolled</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {stats.enrolled.toLocaleString('en-IN')}
              </p>
              {stats.total > 0 && (
                <p className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-0.5 font-mono tabular-nums">
                  {Math.round((stats.enrolled / stats.total) * 100)}% of users
                </p>
              )}
            </div>
          </button>

          <button
            type="button"
            onClick={() => { setMfaFilter('required'); setPage(1); }}
            aria-label={`Filter to ${stats.required.toLocaleString('en-IN')} users with MFA enforcement active`}
            className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden text-left transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400')}
            style={{ animationDelay: '100ms' }}
          >
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', violetTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', violetTone.icon)}>
                  <LockIcon />
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">Enforcement Active</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {stats.required.toLocaleString('en-IN')}
              </p>
              {stats.total > 0 && (
                <p className="text-[11px] text-violet-600 dark:text-violet-400 mt-0.5 font-mono tabular-nums">
                  {Math.round((stats.required / stats.total) * 100)}% required
                </p>
              )}
            </div>
          </button>

          <button
            type="button"
            onClick={() => { setMfaFilter('non_compliant'); setPage(1); }}
            aria-label={`Filter to ${stats.nonCompliant.toLocaleString('en-IN')} non-compliant users — required but not enrolled`}
            className={cn(GLASS, 'p-5 animate-slide-up relative overflow-hidden text-left transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400')}
            style={{ animationDelay: '150ms' }}
          >
            <div className={cn('absolute inset-0 bg-gradient-to-br opacity-40 pointer-events-none', dangerTone.accent)} />
            <div className="relative">
              <div className="flex items-center gap-3 mb-2">
                <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', dangerTone.icon)}>
                  <AlertIcon />
                </div>
                <span className="text-xs sm:text-sm font-medium text-neutral-500 dark:text-neutral-400">Non-compliant</span>
              </div>
              <p className="text-2xl font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                {stats.nonCompliant.toLocaleString('en-IN')}
              </p>
              <p className="text-[11px] text-rose-600 dark:text-rose-400 mt-0.5">
                Required but not yet enrolled
              </p>
            </div>
          </button>
        </div>

        {/* Filters */}
        <div className={cn(GLASS_FILTER, 'p-4 mb-6')}>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] max-w-sm">
              <Input
                variant="search"
                placeholder="Search by name or email"
                value={searchInput}
                onChange={(val) => setSearchInput(val)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearch(searchInput.trim());
                    setPage(1);
                  }
                }}
              />
            </div>
            <div className="w-44">
              <Select
                options={ALL_ROLES}
                value={roleFilter}
                onChange={(val) => {
                  setRoleFilter(typeof val === 'string' ? val : val[0] ?? '');
                  setPage(1);
                }}
                placeholder="All roles"
              />
            </div>
            <div className="w-48">
              <Select
                options={MFA_STATUS_OPTIONS}
                value={mfaFilter}
                onChange={(val) => {
                  setMfaFilter((typeof val === 'string' ? val : val[0] ?? '') as MfaStatusFilter);
                  setPage(1);
                }}
                placeholder="All MFA states"
              />
            </div>
            {isPlatformRole && tenants.length > 0 && (
              <div className="w-56">
                <Select
                  options={tenantOptions}
                  value={tenantFilter}
                  onChange={(val) => {
                    setTenantFilter(typeof val === 'string' ? val : val[0] ?? '');
                    setPage(1);
                  }}
                  placeholder="All tenants"
                />
              </div>
            )}
            {(search || roleFilter || mfaFilter || tenantFilter) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setRoleFilter('');
                  setMfaFilter('');
                  setTenantFilter('');
                  setPage(1);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Table / loading / empty */}
        {isLoading ? (
          <div className={cn(GLASS, 'p-6')}>
            <div className="space-y-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <Skeleton variant="circle" width={36} height={36} />
                  <Skeleton variant="text" width="22%" height={18} />
                  <Skeleton variant="rect" width={64} height={20} />
                  <Skeleton variant="text" width="12%" height={16} />
                  <Skeleton variant="text" width="14%" height={16} />
                  <Skeleton variant="text" width="10%" height={16} />
                  <Skeleton variant="rect" width={120} height={28} />
                </div>
              ))}
            </div>
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            variant="page"
            icon={<ShieldEmptyIcon />}
            title="No users match these filters"
            description="Adjust the role or MFA state filters above, or clear them to see the full list."
          />
        ) : (
          <>
            <div className={cn(GLASS, 'overflow-hidden mb-6')}>
              <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap">
                <h2 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                  Users ({users.length.toLocaleString('en-IN')}{users.length !== totalItems ? ` / ${totalItems.toLocaleString('en-IN')}` : ''})
                </h2>
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-neutral-400 dark:text-neutral-500 hidden sm:inline">
                    Sessions sign out automatically when MFA is newly required
                  </span>
                  <Link
                    to="/audit?entity_type=USER&action=UPDATE"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors"
                  >
                    <ShieldCheckIcon className="h-3.5 w-3.5" />
                    View audit trail
                  </Link>
                </div>
              </div>
              <DataTable<Row>
                columns={columns}
                data={users.map((u) => ({ ...u, id: u.id })) as Row[]}
              />
            </div>

            {totalPages > 1 && (
              <div className="mb-6">
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Per-row confirm dialog */}
      <ConfirmDialog
        open={!!confirmState}
        title={
          confirmState?.type === 'require_on' ? 'Require MFA?'
          : confirmState?.type === 'require_off' ? 'Make MFA optional?'
          : confirmState?.type === 'enable_totp' ? 'Enable TOTP slot?'
          : 'Reset MFA enrollment?'
        }
        description={
          confirmState?.type === 'require_on'
            ? `${confirmState.user.name} will be forced to enroll TOTP on next login. Active sessions will be signed out immediately.`
          : confirmState?.type === 'require_off'
            ? `${confirmState.user.name} will no longer be required to use MFA. Existing enrollment is kept.`
          : confirmState?.type === 'enable_totp'
            ? `${confirmState.user.name} will be able to complete TOTP setup on their next login. They will not be locked out before that.`
          : confirmState?.type === 'disable_totp'
            ? `${confirmState.user.name}'s TOTP secret will be cleared. They can re-enroll any time and will be prompted again on next login if MFA is required.`
          : ''
        }
        variant={confirmState?.type === 'disable_totp' || confirmState?.type === 'require_on' ? 'danger' : 'warning'}
        confirmLabel={
          confirmState?.type === 'require_on' ? 'Require MFA'
          : confirmState?.type === 'require_off' ? 'Make optional'
          : confirmState?.type === 'enable_totp' ? 'Enable slot'
          : 'Reset enrollment'
        }
        cancelLabel="Cancel"
        loading={confirmLoading}
        onConfirm={() => { if (confirmState) void runMutation(confirmState); }}
        onCancel={() => { if (!confirmLoading) setConfirmState(null); }}
      />

      {/* Bulk modal */}
      {callerLevel >= ROLE_LEVELS.OWNER && callerId && (
        <BulkRequireModal
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          onCompleted={() => { void fetchUsers(); refreshStats(); }}
          callerRole={callerRole}
          callerId={callerId}
          tenantScope={tenantFilter || undefined}
        />
      )}
    </PageContainer>
  );
}

export function MfaEnforcementPage() {
  return (
    <ErrorBoundary>
      <MfaEnforcementContent />
    </ErrorBoundary>
  );
}
